import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { DatePicker } from '../../../components/DatePicker';
import { leadSelectDropdownFields } from '../../../utils/leadSelectOption';
import {
  rebaseTaskRecurrence,
  taskRecurrenceFromPreset,
  taskRecurrenceOptions,
  taskRecurrencePreset,
  type TaskRecurrence,
} from '../../../utils/taskRecurrence';
import { UI_Z_INDEX } from '../../../utils/uiZIndex';
import type { RecurrencePresetId } from '../../../utils/activityRecurrence';
import { PRIORITY_META, PRIORITY_ORDER, leadLabel, memberName } from '../taskViews';
import type { TaskLead, TaskLeadOption, TaskMember, TaskPatch, TaskPriority } from '../types';
import { SegmentedControl } from './taskUi';

const PRIORITY_OPTIONS = PRIORITY_ORDER.map((p) => ({ value: p, label: PRIORITY_META[p].label }));
const FIELD_TRIGGER_CLASS = '!min-h-0 h-8 !py-0';

export type TaskFieldValues = {
  due_date: string | null;
  assigned_user_id: string | null;
  priority: TaskPriority;
  lead: TaskLead | null;
  recurrence: TaskRecurrence | null;
};

type FieldPatch = Pick<TaskPatch, 'due_date' | 'assigned_user_id' | 'priority' | 'lead_id' | 'recurrence'>;

/** Rahmen des rechten Panels — Bearbeiten und Anlegen teilen sich Optik und Position. */
export function TaskPanelFrame({
  overlay,
  label,
  header,
  footer,
  onBack,
  children,
}: {
  overlay: boolean;
  label: string;
  header: React.ReactNode;
  footer?: React.ReactNode;
  /** Zurück-Pfeil links in der Kopfzeile — nur mobil, wo das Panel den ganzen Bildschirm füllt. */
  onBack: () => void;
  children: React.ReactNode;
}) {
  return (
    <aside
      aria-label={label}
      className={`flex w-[min(380px,100%)] shrink-0 flex-col overflow-hidden border-l border-app-border bg-app-surface max-md:absolute max-md:inset-0 max-md:z-20 max-md:w-full max-md:border-l-0 ${
        overlay ? 'absolute inset-y-0 right-0 z-10 shadow-xl max-md:shadow-none' : 'relative'
      }`}
    >
      <div className="flex h-12 shrink-0 items-center gap-1 pl-5 pr-3 max-md:pl-2">
        <PanelIconButton title="Zurück" onClick={onBack} className="md:hidden">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
        </PanelIconButton>
        <div className="flex min-w-0 flex-1 items-center justify-between">{header}</div>
      </div>
      <div className="app-scrollbar flex flex-1 flex-col gap-5 overflow-y-auto overscroll-contain px-5 pb-5 pt-1">
        {children}
      </div>
      {footer}
    </aside>
  );
}

export function PanelIconButton({
  title,
  onClick,
  danger,
  className = '',
  children,
}: {
  title: string;
  onClick: () => void;
  danger?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-app-text-subtle transition-colors max-md:h-10 max-md:w-10 ${
        danger ? 'hover:bg-red-50 hover:text-red-600' : 'hover:bg-app-hover hover:text-app-text'
      } ${className}`}
    >
      {children}
    </button>
  );
}

/** Titelfeld ohne Rahmen, der erst bei Hover/Fokus erscheint (wie im Entwurf). */
export const TASK_TITLE_INPUT_CLASS =
  '-mx-1.5 -my-1 w-[calc(100%+12px)] resize-none rounded-md border border-transparent px-1.5 py-1 text-lg font-semibold leading-snug text-app-text outline-none transition-colors placeholder:text-app-text-subtle hover:border-app-border focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20 read-only:hover:border-transparent';

type Props = {
  values: TaskFieldValues;
  onChange: (patch: FieldPatch, lead?: TaskLead | null) => void;
  members: TaskMember[];
  leadOptions: TaskLeadOption[];
  canEdit: boolean;
  canOpenProjects: boolean;
  instancePrefix: string;
  /** Zusätzliche Zeilen vor den Feldern (z. B. Status). */
  leading?: React.ReactNode;
};

/** Eigenschaften-Raster: Fällig am, Wiederholen, Zuständig, Priorität, Verknüpft. */
export function TaskFields({
  values,
  onChange,
  members,
  leadOptions,
  canEdit,
  canOpenProjects,
  instancePrefix,
  leading,
}: Props) {
  const memberOptions = React.useMemo(
    () => [
      { value: '', label: 'Nicht zugewiesen' },
      ...members.map((m) => ({ value: m.id, label: memberName(m), searchText: m.email })),
    ],
    [members],
  );
  // Neue Fälligkeit: Wiederholung bleibt dieselbe Auswahl am neuen Datum; ohne Datum keine Wiederholung.
  const changeDue = (due: string | null) =>
    onChange({
      due_date: due,
      ...(values.recurrence ? { recurrence: rebaseTaskRecurrence(values.recurrence, values.due_date, due) } : {}),
    });
  const leadSelectOptions = React.useMemo(
    () => leadOptions.map((l) => ({ value: l.id, ...leadSelectDropdownFields(l) })),
    [leadOptions],
  );

  return (
    <div className="grid grid-cols-[96px_minmax(0,1fr)] items-center gap-x-3 gap-y-2.5 text-sm">
      {leading}

      <span className="text-app-text-subtle">Fällig am</span>
      <div className="flex min-w-0 items-center gap-1">
        <DatePicker
          value={values.due_date ?? ''}
          onChange={(v) => v !== values.due_date && changeDue(v || null)}
          disabled={!canEdit}
          placeholder="Kein Datum"
          menuZIndex={UI_Z_INDEX.portalPanel}
          className="min-w-0 flex-1"
          inputClassName="!h-8"
        />
        {canEdit && values.due_date && (
          <button
            type="button"
            onClick={() => changeDue(null)}
            title="Datum entfernen"
            aria-label="Datum entfernen"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <span className="text-app-text-subtle">Wiederholen</span>
      {values.due_date ? (
        <AppSelectDropdown
          value={taskRecurrencePreset(values.recurrence, values.due_date)}
          onChange={(preset) =>
            onChange({
              recurrence: taskRecurrenceFromPreset(
                preset as RecurrencePresetId,
                values.due_date as string,
                values.recurrence?.until,
              ),
            })
          }
          options={taskRecurrenceOptions(values.recurrence, values.due_date)}
          disabled={!canEdit}
          triggerClassName={FIELD_TRIGGER_CLASS}
        />
      ) : (
        <span className="text-xs text-app-text-subtle">Erst ein Fälligkeitsdatum wählen</span>
      )}

      {values.recurrence && values.due_date && (
        <>
          <span className="text-app-text-subtle">Endet am</span>
          <div className="flex min-w-0 items-center gap-1">
            <DatePicker
              value={values.recurrence.until ?? ''}
              onChange={(v) => {
                const until = v && v >= (values.due_date as string) ? v : undefined;
                if (until === values.recurrence?.until) return;
                const { until: _until, ...rest } = values.recurrence as TaskRecurrence;
                onChange({ recurrence: until ? { ...rest, until } : rest });
              }}
              disabled={!canEdit}
              placeholder="Nie"
              menuZIndex={UI_Z_INDEX.portalPanel}
              className="min-w-0 flex-1"
              inputClassName="!h-8"
            />
            {canEdit && values.recurrence.until && (
              <button
                type="button"
                onClick={() => {
                  const { until: _until, ...rest } = values.recurrence as TaskRecurrence;
                  onChange({ recurrence: rest });
                }}
                title="Ende entfernen"
                aria-label="Ende entfernen"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </>
      )}

      <span className="text-app-text-subtle">Zuständig</span>
      <AppSelectDropdown
        value={values.assigned_user_id ?? ''}
        onChange={(v) => onChange({ assigned_user_id: v || null })}
        options={memberOptions}
        disabled={!canEdit}
        searchable={members.length > 6}
        searchPlaceholder="Mitarbeiter suchen…"
        triggerClassName={FIELD_TRIGGER_CLASS}
      />

      <span className="text-app-text-subtle">Priorität</span>
      <SegmentedControl<TaskPriority>
        options={PRIORITY_OPTIONS}
        value={values.priority}
        onChange={(p) => p !== values.priority && onChange({ priority: p })}
        disabled={!canEdit}
        ariaLabel="Priorität"
        fullWidth
      />

      <span className="self-start pt-2 text-app-text-subtle">Verknüpft</span>
      {values.lead ? (
        <div className="flex min-w-0 items-stretch gap-1">
          {canOpenProjects ? (
            <Link
              to={`${instancePrefix}/projects/${values.lead.id}`}
              className="flex min-w-0 flex-1 flex-col gap-px rounded-md border border-app-border px-2.5 py-2 transition-colors hover:bg-app-hover"
            >
              <LeadCardContent lead={values.lead} />
            </Link>
          ) : (
            <div className="flex min-w-0 flex-1 flex-col gap-px rounded-md border border-app-border px-2.5 py-2">
              <LeadCardContent lead={values.lead} />
            </div>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => onChange({ lead_id: null }, null)}
              title="Verknüpfung entfernen"
              aria-label="Verknüpfung entfernen"
              className="flex w-8 shrink-0 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ) : canEdit ? (
        <AppSelectDropdown
          value=""
          onChange={(id) => {
            const lead = leadOptions.find((l) => l.id === id);
            if (lead) onChange({ lead_id: lead.id }, lead);
          }}
          options={leadSelectOptions}
          placeholder="+ Anfrage verknüpfen"
          searchable
          searchPlaceholder="Anfrage suchen…"
          className="w-full min-w-0"
          triggerClassName={`${FIELD_TRIGGER_CLASS} !border-dashed text-xs text-app-text-subtle`}
        />
      ) : (
        <span className="text-app-text-subtle">—</span>
      )}
    </div>
  );
}

function LeadCardContent({ lead }: { lead: TaskLead }) {
  return (
    <>
      <span className="truncate text-sm font-medium text-pipedrive-blue">
        {lead.lead_title?.trim() || leadLabel(lead)}
        {lead.display_id ? ` · ${lead.display_id}` : ''}
      </span>
      {lead.name && <span className="truncate text-xs text-app-text-subtle">{lead.name}</span>}
    </>
  );
}
